import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Megaphone } from 'lucide-react';
import BannerDisplay from './BannerDisplay';
import { AD_POOL_LIMIT, AD_ROTATION_SECONDS } from './constants';
import { useAdSlots } from './useAdSlots';

/**
 * Always-visible ad strip for mobile and tablet viewports (below xl).
 * Lives inside MainLayout above the page Outlet so every route sees it —
 * unlike the previous "More menu" placement which required users to open a menu first.
 *
 * Cycles through the 3 sidebar slots every 10s so each gets airtime without hogging
 * vertical space on small screens. Desktop (xl+) uses the full RightSidebar instead.
 */
const SLOTS = ['sidebar_top', 'sidebar_middle', 'sidebar_bottom'];
const initialAdvanceSignals = SLOTS.reduce<Record<string, number>>((signals, slot) => {
    signals[slot] = 0;
    return signals;
}, {});

const MobileAdStrip: React.FC = () => {
    const [activeSlot, setActiveSlot] = useState(0);
    const [advanceSignals, setAdvanceSignals] = useState<Record<string, number>>(initialAdvanceSignals);
    const { adsBySlot, countsBySlot: slotAdCounts, loading } = useAdSlots(SLOTS, {
        limit: AD_POOL_LIMIT,
    });
    const slotAdCountsRef = useRef(slotAdCounts);

    useEffect(() => {
        slotAdCountsRef.current = slotAdCounts;
    }, [slotAdCounts]);

    const getNextAvailableSlot = useCallback((fromIndex: number, counts: Record<string, number>) => {
        for (let step = 1; step <= SLOTS.length; step += 1) {
            const nextIndex = (fromIndex + step) % SLOTS.length;
            const nextSlot = SLOTS[nextIndex];

            if ((counts[nextSlot] ?? 0) > 0) {
                return nextIndex;
            }
        }

        return fromIndex;
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            setActiveSlot((prev) => {
                const currentSlot = SLOTS[prev];
                setAdvanceSignals((signals) => ({
                    ...signals,
                    [currentSlot]: (signals[currentSlot] ?? 0) + 1,
                }));

                return getNextAvailableSlot(prev, slotAdCountsRef.current);
            });
        }, AD_ROTATION_SECONDS * 1000);

        return () => clearInterval(id);
    }, [getNextAvailableSlot]);

    useEffect(() => {
        const currentSlot = SLOTS[activeSlot];

        if (slotAdCounts[currentSlot] === 0 && SLOTS.some((slot) => (slotAdCounts[slot] ?? 0) > 0)) {
            setActiveSlot((prev) => getNextAvailableSlot(prev, slotAdCounts));
        }
    }, [activeSlot, getNextAvailableSlot, slotAdCounts]);

    const allSlotsLoaded = !loading;
    const allSlotsEmpty = allSlotsLoaded && SLOTS.every((slot) => slotAdCounts[slot] === 0);

    if (allSlotsEmpty) {
        return null;
    }

    return (
        <div className="xl:hidden mb-3 sm:mb-4">
            <div className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl border border-gray-200 dark:border-[#1e2a5e] overflow-hidden shadow-sm">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-[#1e2a5e]">
                    <div className="p-1 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-md">
                        <Megaphone className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[10px] sm:text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                        Sponsored
                    </span>
                    <div className="ml-auto flex items-center gap-1">
                        {SLOTS.map((_, i) => (
                            <span
                                key={i}
                                className={`w-1.5 h-1.5 rounded-full transition-all ${slotAdCounts[SLOTS[i]] === 0
                                    ? 'bg-gray-200 dark:bg-gray-700 opacity-40'
                                    : i === activeSlot
                                    ? 'bg-emerald-500 w-4'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
                <div className="p-2 sm:p-3">
                    <div className="relative isolate w-full overflow-hidden rounded-2xl bg-gray-100 dark:bg-[#080c22]" style={{ height: 180 }}>
                        {SLOTS.map((slot, index) => (
                            <motion.div
                                key={slot}
                                className={`absolute inset-0 transition-opacity duration-200 ${index === activeSlot ? 'opacity-100 pointer-events-auto visible z-10' : 'opacity-0 pointer-events-none invisible z-0'
                                    }`}
                                initial={false}
                                animate={{ opacity: index === activeSlot ? 1 : 0 }}
                                transition={{ duration: 0.2 }}
                                style={{ zIndex: index === activeSlot ? 1 : 0 }}
                                aria-hidden={index !== activeSlot}
                            >
                                <BannerDisplay
                                    placement={slot}
                                    width="100%"
                                    height={180}
                                    rotationInterval={AD_ROTATION_SECONDS}
                                    limit={AD_POOL_LIMIT}
                                    isVisible={index === activeSlot}
                                    autoRotate={false}
                                    advanceSignal={advanceSignals[slot]}
                                    ads={adsBySlot[slot] ?? []}
                                    loading={loading}
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MobileAdStrip;
